import { Button, Card, Icon, Link, Tag, Text } from "@ttc3k/trekker";
import { ArrowRight, MapPin, OpenNewWindow } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { Box, BoxProps, Flex, styled } from "styled-system/jsx";
import { operatorCardRecipe, OperatorCardRecipeProps } from "./recipe";
import {
  EnumOperatorOperationalStatus,
  OperatorWithOverrides,
} from "gql/generated";
import {
  formatDistance,
  getFormattedDetailsLink,
  getMultilingualString,
} from "shared/utils";
import { SmartImage } from "shared/components/atoms";
import { MultilingualLangKey, DistanceUnit } from "shared/types";
import { OperatorsWidgetDetailsDisplayMode } from "widgets/operators/OperatorsWidget";
import { PostHogCustomEvent } from "shared/hooks/usePostHog";
import { useWidgetTrack } from "widgets/shared/WidgetTrackingContext";
import { useTrackImpression } from "shared/hooks/useTrackImpression";
import { useCalculateDistance } from "shared/hooks";
import { useOperatorPromotions } from "widgets/shared/WidgetPromotionsContext";
import _ from "lodash";

const StyledLink = styled(Link, {
  base: {
    display: "flex",
    gap: "50",
    color: "text.dark",
    fontWeight: "semiBold",
    transitionDuration: "normal",
    transitionProperty: "color",
    transitionTimingFunction: "default",
    _hover: {
      color: "text.accent.blue.mid",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

type OperatorCardProps = OperatorCardRecipeProps & {
  operator: Partial<OperatorWithOverrides>;
  onClick?: (operatorId: string) => void;
  viewOnly?: boolean;
  cardSurfaceProps?: BoxProps;
  lang?: MultilingualLangKey;
  operatorDetailsDisplayMode?: OperatorsWidgetDetailsDisplayMode;
  operatorDetailsLink?: string;
  organizationId?: string;
  widgetId?: string;
  startingCoordinates?: [number, number]; // [latitude, longitude]
  distanceUnit?: DistanceUnit;
  ignoreImageTextAndNeverBlur?: boolean;
};

export const OperatorWidgetCard = ({
  operator,
  onClick,
  orientation,
  viewOnly = false,
  cardSurfaceProps,
  lang = "en",
  operatorDetailsDisplayMode,
  operatorDetailsLink,
  organizationId,
  widgetId,
  startingCoordinates,
  distanceUnit,
  ignoreImageTextAndNeverBlur: _ignoreImageTextAndNeverBlur = false,
}: OperatorCardProps) => {
  const classes = operatorCardRecipe({ orientation });
  const { t } = useTranslation();
  const trackPostHogEvent = useWidgetTrack();
  const cardRef = useTrackImpression({
    organizationId,
    widgetId,
    operatorEntityId: operator._entityId,
  });

  const operatorName = getMultilingualString(operator.name ?? {}, lang);

  // A real href is always rendered when a details-link template is configured
  // (even in slideout mode) so crawlers can discover the hosted pages.
  const detailsHref = operatorDetailsLink
    ? getFormattedDetailsLink(
        operatorName,
        operatorDetailsLink,
        operator._entityId,
      )
    : undefined;
  const isLinkMode =
    !!operatorDetailsLink && operatorDetailsDisplayMode === "link";

  function trackDetailsClick() {
    if (viewOnly) return;
    trackPostHogEvent(
      operatorDetailsDisplayMode === "slideout"
        ? PostHogCustomEvent.DetailsClick
        : PostHogCustomEvent.ExpandedDetailsClick,
      {
        organizationId: organizationId,
        operatorEntityId: operator?._entityId,
        widgetId: widgetId,
      },
    );
  }

  function handleDetailsClick(e: React.MouseEvent) {
    trackDetailsClick();
    // Link mode: let native anchor navigation open the hosted page
    // (also keeps cmd/middle-click behavior intact).
    if (isLinkMode) return;
    // Slideout mode: the href exists for crawlers and new-tab clicks only.
    e.preventDefault();
    onClick!(operator._entityId);
  }

  // Space/Enter support. Enter already fires a native click on anchors with
  // an href, so only handle it when there is no href (else it double-fires).
  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && detailsHref) return;
    if (e.key !== " " && e.key !== "Enter") return;
    trackDetailsClick();
    if (isLinkMode) {
      window.open(detailsHref, "_blank", "noopener");
    } else {
      onClick!(operator._entityId);
    }
  };

  /* TODO: when Booker is added, handle click for Booker vs. Book now link */
  const handleBookNowClick = () => {
    if (!operator.contact?.links?.bookNow) return;
    trackPostHogEvent(PostHogCustomEvent.OpenBookNowLink, {
      bookNowLink: operator.contact.links.bookNow,
    });
    window.open(operator.contact.links.bookNow, "_blank", "noopener");
  };

  const previewImage =
    operator.overrides?.images && operator.overrides.images.length > 0
      ? operator.overrides.images[0]
      : (operator.images?.[0] ??
        (
          operator as Partial<OperatorWithOverrides> & {
            fallbackImage?: Partial<
              OperatorWithOverrides["images"][number]
            > | null;
          }
        ).fallbackImage);
  const hideTeaserImage =
    (
      operator as Partial<OperatorWithOverrides> & {
        tierDisplayOverride?: {
          teaser?: { hideImage?: boolean | null } | null;
        };
      }
    ).tierDisplayOverride?.teaser?.hideImage ?? false;

  const shortDescription = operator.overrides?.shortDescription
    ? getMultilingualString(operator.overrides.shortDescription, lang)
    : getMultilingualString(operator.shortDescription ?? {}, lang);

  const isPortraitOrientation = orientation === "portrait";

  const isClosed =
    operator.operationalStatus ===
      EnumOperatorOperationalStatus.PermanentlyClosed ||
    operator.operationalStatus ===
      EnumOperatorOperationalStatus.TemporarilyClosed;
  const closureStatusBadge = isClosed ? (
    <Tag
      visual={
        operator.operationalStatus ===
        EnumOperatorOperationalStatus.PermanentlyClosed
          ? "destructive"
          : "warning"
      }
      mode="light"
      size="sm"
      part="card__status-tag"
    >
      {t(
        `core:LOOKUPS.OPERATOR_OPERATIONAL_STATUS.${operator.operationalStatus}`,
      )}
    </Tag>
  ) : undefined;

  // Approved, active promotions for this listing (empty outside a widget that
  // provides them). Shown as a badge over the photo, or beside the title when
  // the tier hides the photo.
  const hasPromotion = useOperatorPromotions(operator._entityId).length > 0;
  const promotionBadge = hasPromotion ? (
    <Tag visual="accent.blue" size="sm" part="card__promotion-badge">
      {t("widgets:OPERATOR.CARD.PROMOTION_BADGE")}
    </Tag>
  ) : undefined;

  // The Card.Hero has a lot of unneeded css properties when the orientation is
  // horizontal they are not needed so use a Box in this case
  const HeroContainer = isPortraitOrientation ? Card.Hero : Box;

  const { distance } = useCalculateDistance(
    startingCoordinates,
    operator.contact?.address?.location?.coordinates &&
      _.compact(operator.contact.address.location.coordinates).length === 2
      ? [
          _.compact(operator.contact.address.location.coordinates)[1],
          _.compact(operator.contact.address.location.coordinates)[0],
        ]
      : undefined,
  );

  return (
    <Card.Surface
      ref={viewOnly ? undefined : cardRef}
      part={"card__surface"}
      key={operator._entityId}
      className={classes.surface}
      height={"full"}
      role="article"
      aria-label={getMultilingualString(operator.name ?? {}, lang)}
      {...cardSurfaceProps}
    >
      {!hideTeaserImage && (
        <HeroContainer
          part={"card__hero"}
          className={classes.hero}
          position="relative"
        >
          {promotionBadge && (
            <Box position="absolute" top="20px" left="20px" zIndex={1}>
              {promotionBadge}
            </Box>
          )}
          <SmartImage
            image={previewImage}
            width={isPortraitOrientation ? 400 : 240}
            height={isPortraitOrientation ? 200 : 262}
            shape="square"
            previewVideo={true}
            alt={operatorName}
            parts={{
              container: "card__image-container",
              frame: "card__image-frame",
              image: "card__image",
            }}
          />
        </HeroContainer>
      )}
      <Card.Body className={classes.body} part={"card__body"}>
        <Box className={classes.textContent} part={"card__text-content"}>
          <Flex
            gap="100"
            alignItems="center"
            flexWrap="wrap"
            mb={
              closureStatusBadge || (hideTeaserImage && promotionBadge)
                ? "100"
                : undefined
            }
          >
            <Text
              visual={"bodySemiBold"}
              lineClamp={2}
              part={"card__title"}
              role="heading"
              aria-level={3}
            >
              {operatorName}
            </Text>
            {closureStatusBadge}
            {hideTeaserImage && promotionBadge}
          </Flex>
          {!isClosed && (
            <Text
              visual={"smallMedium"}
              lineClamp={isPortraitOrientation ? 2 : 3}
              color={"text.mid"}
              part={"card__description"}
            >
              {shortDescription}
            </Text>
          )}
        </Box>
        <Card.BodyContent
          gap={"200"}
          minH={"38px"}
          alignItems={"center"}
          part="card__body-content"
        >
          <StyledLink
            onClick={handleDetailsClick}
            onKeyUp={handleKeyUp}
            href={detailsHref}
            target={isLinkMode ? "_blank" : undefined}
            rel={isLinkMode ? "noopener" : undefined}
            part={"card__btn-details"}
            tabIndex="0"
            aria-label={t("widgets:OPERATORS_WIDGET.ARIA.VIEW_DETAILS", {
              name: operatorName,
            })}
          >
            {t("widgets:OPERATOR.CARD.SEE_DETAILS")}
            <Icon
              Element={
                operatorDetailsDisplayMode === "link"
                  ? OpenNewWindow
                  : ArrowRight
              }
            ></Icon>
          </StyledLink>
          {!!distance && distance > 0 && (
            <Flex gap={"50"} alignItems={"center"}>
              <Icon
                Element={MapPin}
                color={"icon.accent.blue.dark"}
                size={"sm"}
                part={"card__distance-icon"}
              />
              <Text
                visual={"smallSemiBold"}
                part={"card__distance-text"}
                color={"text.accent.blue.dark"}
              >
                {t("widgets:WHATS_NEARBY.DISTANCE_AWAY", {
                  distance: formatDistance(distance, distanceUnit),
                })}
              </Text>
            </Flex>
          )}
          {operator.contact?.links?.bookNow && (
            <Button
              visual={"accent.blue"}
              onClick={handleBookNowClick}
              part={"card__btn-book"}
            >
              {t("widgets:OPERATOR.CARD.BOOK_NOW")}
            </Button>
          )}
        </Card.BodyContent>
      </Card.Body>
    </Card.Surface>
  );
};
